import { useDashboardSurveyList } from "./useDashboardSurveyList";
import { DASHBOARD_SURVEY_LIST_PAGE_SIZE } from "./dashboard-survey-list.constants";
import { Survey, SurveyStatus } from "../../../redux/survey/survey.types";
import { constant, times } from "lodash";
import DashboardSurveyFilter from "./dashboard-survey-filter.component";
import DashboardSurveyPagination from "./dashboard-survey-pagination.component";
import DashboardSurveyListEmpty from "./dashboard-survey-list-empty.component";
import SurveyCard from "./survey-card.component";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/ui/table";
import { Button } from "src/components/ui/button";
import { LayoutGrid, Table as TableIcon } from "lucide-react";
import { format } from "date-fns";
import { useState } from "react";
import { Link } from "react-router-dom";
import { canEditSurvey } from "../../../redux/survey/survey.helpers";
import SurveyCardMenu from "./survey-card-menu.component";
import { Skeleton } from "src/components/ui/skeleton";

const DashboardSurveyTable = () => {
  const { data, isFetching } = useDashboardSurveyList();
  const surveys = data?.surveys ?? [];
  const loadingArray = times(DASHBOARD_SURVEY_LIST_PAGE_SIZE, constant(undefined));
  const isEmpty = data?.totalObjects === 0;
  const [isTableView, setIsTableView] = useState(true);

  const getStatusColor = (status: SurveyStatus, isDot: boolean = false) => {
    switch (status) {
      case SurveyStatus.Draft:
        return isDot ? 'bg-gray-600' : 'text-gray-500';
      case SurveyStatus.Live:
        return isDot ? 'bg-blue-500' : 'text-blue-500';
      case SurveyStatus.Complete:
        return isDot ? 'bg-[#31BB64]' : 'text-[#31BB64]';
      default:
        return isDot ? 'bg-gray-600' : 'text-gray-600';
    }
  };

  const renderTable = (surveys: (Survey | undefined)[]) => {
    return (
      <div className="w-full bg-white rounded-xl border border-gray-200 p-4 pt-2">
        <Table>
          <TableHeader>
            <TableRow className="border-none">
              <TableHead className="w-[78px] max-w-[78px] py-1.5" />
              <TableHead className="text-gray-600 py-1.5">
                Title
              </TableHead>
              <TableHead className="text-gray-600 py-1.5">
                Created
              </TableHead>
              <TableHead className="text-gray-600 text-center py-1.5">
                Questions
              </TableHead>
              <TableHead className="text-gray-600 text-center py-1.5">
                Starts
              </TableHead>
              <TableHead className="text-gray-600 text-center py-1.5">
                Completes
              </TableHead>
              <TableHead className="w-[30px] py-1.5" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {surveys.map((survey, index) => (
              <TableRow
                key={`survey-row-${index}`}
                className={`hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 ${
                  index % 2 === 0 ? 'bg-[#F8F9FA]' : 'bg-[#F2F3F5]'
                } cursor-pointer`}
              >
                <Link
                  to={survey ? (canEditSurvey(survey.surveyStatus) ? `/create-survey` : '/view-survey') + `?ext-survey-id=${survey.extSurveyId}` : '#'}
                  style={{ display: 'contents' }}
                  aria-label={survey ? `View survey ${survey.title}` : undefined}
                  tabIndex={survey ? undefined : -1}
                >
                  <TableCell className={`text-center py-1.5 ${index === 0 ? 'rounded-tl-xl' : index === surveys.length - 1 ? 'rounded-bl-xl' : ''}`}>
                    {survey ? (
                      <span
                        className={`max-w-[78px] inline-flex items-center justify-center rounded-full py-1 px-2 text-xs font-bold bg-white shadow-sm ${getStatusColor(survey.surveyStatus)}`}
                      >
                        { survey.surveyStatus !== SurveyStatus.Draft && (<div className={`size-1 rounded-full mr-1 ${getStatusColor(survey.surveyStatus, true)}`} />)}
                        {survey.surveyStatus.charAt(0).toUpperCase() + survey.surveyStatus.slice(1)}
                      </span>
                    ) : (
                      <Skeleton className="w-16 h-[26px] rounded-full mx-auto" />
                    )}
                  </TableCell>
                  <TableCell className="font-medium py-1.5">
                    {survey ? survey.title : <Skeleton className="w-48 h-4 rounded" />}
                  </TableCell>
                  <TableCell className="py-1.5">
                    {survey ? format(new Date(survey.createdAt), 'MMM d, yyyy') : <Skeleton className="w-24 h-4 rounded" />}
                  </TableCell>
                  <TableCell className="text-center py-1.5">
                    {survey ? survey.questionCount : <Skeleton className="w-8 h-4 rounded mx-auto" />}
                  </TableCell>
                  <TableCell className="text-center py-1.5">
                    {survey ? (
                      (survey.surveyStatus === SurveyStatus.Live || survey.surveyStatus === SurveyStatus.Complete) ? (
                        <span className="text-sm">
                          {survey.started.human + survey.started.ai}
                        </span>
                      ) : (
                        <span className="text-sm">
                          -
                        </span>
                      )
                    ) : <Skeleton className="w-8 h-4 rounded mx-auto" />}
                  </TableCell>
                  <TableCell className="text-center py-1.5">
                    {survey ? (
                      (survey.surveyStatus === SurveyStatus.Live || survey.surveyStatus === SurveyStatus.Complete) ? (
                        <span className="text-sm">
                          {survey.completed.human + survey.completed.ai}
                        </span>
                      ) : (
                        <span className="text-sm">
                          -
                        </span>
                      )
                    ) : <Skeleton className="w-8 h-4 rounded mx-auto" />}
                  </TableCell>
                  <TableCell
                    className={`${index === 0 ? 'rounded-tr-xl' : index === surveys.length - 1 ? 'rounded-br-xl' : ''} py-1.5`}
                    onClick={e => e.stopPropagation()}
                  >
                    {survey ? <SurveyCardMenu survey={survey} /> : <Skeleton className="w-6 h-6 rounded-full mx-auto" />}
                  </TableCell>
                </Link>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderCards = (surveys: (Survey | undefined)[]) => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 w-full">
        {surveys.map((survey, index) => (
          <SurveyCard
            survey={survey}
            key={`survey-card-${index}`}
            index={index}
          />
        ))}
      </div>
    );
  };

  return (
    <div
      className="flex flex-col gap-4 grow"
      id="survey-list"
    >
      <div className="flex items-center justify-end gap-4">
        <div className="flex-1">
          <DashboardSurveyFilter />
        </div>
        <div className="flex items-center gap-1 p-1">
          <Button
            variant={isTableView ? "primary" : "ghost"}
            size="icon"
            className={`h-8 w-8 ${!isTableView ? "text-black/60" : ""}`}
            onClick={() => setIsTableView(true)}
          >
            <TableIcon className="h-4 w-4" />
          </Button>
          <Button
            variant={!isTableView ? "primary" : "ghost"}
            size="icon"
            className={`h-8 w-8 ${isTableView ? "text-black/60" : ""}`}
            onClick={() => setIsTableView(false)}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {isEmpty ? <DashboardSurveyListEmpty /> : isTableView ? renderTable(isFetching ? loadingArray : surveys) : renderCards(isFetching ? loadingArray : surveys)}
      <DashboardSurveyPagination />
    </div>
  );
};

export default DashboardSurveyTable;